import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Linear Momentum",
  examWeight: "10–15%",
  focus: "Inertia in motion, the impulse-momentum theorem, and the conservation of momentum in isolated systems.",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Linear Momentum",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Definition and Mathematical Model</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Linear momentum (symbol <InlineMath math="\vec{p}" />) is often described as <strong>"inertia in motion"</strong> or the <strong>"quantity of motion."</strong> It represents how difficult it is to stop a moving object. It is a <strong>vector quantity</strong>, meaning it has both magnitude and direction.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-semibold text-slate-800 mb-2">Equation</h4>
              <BlockMath math="\vec{p} = m\vec{v}" />
              <ul className="text-sm text-slate-600 mt-2 space-y-1">
                <li><InlineMath math="\vec{p}" /> = Momentum (<InlineMath math="kg \cdot m/s" />)</li>
                <li><InlineMath math="m" /> = Mass (<InlineMath math="kg" />)</li>
                <li><InlineMath math="\vec{v}" /> = Velocity (<InlineMath math="m/s" />)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Vector Nature of Momentum</h3>
            <p className="text-slate-700 mb-4">
              Because momentum is a vector, the direction of the momentum vector is <strong>always</strong> the same as the direction of the velocity vector.
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Sign Conventions</h4>
                <p className="text-sm text-slate-700 mt-1">
                  You must define a positive direction (e.g., right is <InlineMath math="+" />, left is <InlineMath math="-" />).
                </p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-700">
                  <strong>Example:</strong> A 2 kg ball moving right at 3 m/s has <InlineMath math="\vec{p} = +6 \, kg\cdot m/s" />. If it bounces back at 3 m/s, its new momentum is <InlineMath math="\vec{p} = -6 \, kg\cdot m/s" />. The <em>change</em> in momentum is distinct from the momentum itself.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Systems of Objects</h3>
            <p className="text-slate-700 mb-4">
              For a system containing multiple objects, the <strong>total momentum</strong> is the vector sum of the individual momenta.
            </p>
            <div className="bg-white p-4 rounded-lg border border-slate-200 mb-4 text-center">
              <BlockMath math="\vec{p}_{system} = \Sigma \vec{p}_i = \vec{p}_1 + \vec{p}_2 + ..." />
            </div>
            <ImportantNote>
              <strong>Crucial Note:</strong> Two objects with equal mass and equal speed moving in <em>opposite</em> directions have a total system momentum of <strong>zero</strong>.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Change in Momentum and Impulse",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Impulse (<InlineMath math="J" />)</h3>
            <p className="text-slate-700 mb-4">
              Impulse is the mechanism by which momentum is transferred to or from a system. It is defined as the product of the average force exerted on an object and the time interval.
            </p>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-900">Equation</h4>
              <BlockMath math="\vec{J} = \vec{F}_{avg} \Delta t" />
              <p className="text-xs text-slate-600 mt-2">
                <strong>Units:</strong> <InlineMath math="N \cdot s" /> (Newtons <InlineMath math="\cdot" /> seconds). Note that <InlineMath math="1 \, N \cdot s = 1 \, kg \cdot m/s" />.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. The Impulse-Momentum Theorem</h3>
            <p className="text-slate-700 mb-4">
              This theorem states that the impulse exerted on an object is equal to the change in that object's momentum. This is the logical bridge between Newton's Laws and Momentum.
            </p>
            <div className="bg-white p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="\vec{J} = \Delta \vec{p} = \vec{p}_f - \vec{p}_i = m(\vec{v}_f - \vec{v}_i)" />
            </div>
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-blue-100">
              <h4 className="font-bold text-slate-800">Real-World Application: Safety Equipment</h4>
              <p className="text-sm text-slate-700 mt-2">
                <strong>Airbags/Crumple Zones:</strong> In a car crash, the change in momentum (<InlineMath math="\Delta p" />) is fixed. To reduce injury, we need to reduce the Force (<InlineMath math="F" />). By extending the time of impact (<InlineMath math="\Delta t" />) using an airbag, the Force decreases:
              </p>
              <div className="mt-2 text-center">
                <InlineMath math="F = \Delta p / \Delta t" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Newton’s Second Law (Momentum Form)</h3>
            <p className="text-slate-700 mb-2">Newton originally formulated his second law in terms of momentum:</p>
            <div className="bg-slate-800 text-white p-3 rounded-lg inline-block">
              <BlockMath math="\vec{F}_{net} = \frac{\Delta \vec{p}}{\Delta t}" />
            </div>
            <p className="text-sm text-slate-600 mt-2 italic">This shows that net force is the rate of change of momentum.</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Graphical Analysis: Force vs. Time</h3>
            <div className="space-y-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">📍 The Area Under the Curve</h4>
                <p className="text-sm text-slate-700 mt-1">The area between the curve and the horizontal time axis represents the <strong>Impulse</strong> (or change in momentum).</p>
                <ul className="list-disc pl-5 mt-2 text-xs text-slate-600 space-y-1">
                  <li>Area above the t-axis = Positive Impulse.</li>
                  <li>Area below the t-axis = Negative Impulse.</li>
                </ul>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-900">🎓 Calculus Note (Conceptual)</h4>
                <p className="text-xs text-slate-700 mt-1">
                  While AP Physics 1 is algebra-based, impulse is technically the integral of force over time: <InlineMath math="J = \int F dt" />. This explains why we calculate the area for non-constant forces.
                </p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Conservation of Linear Momentum",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. The Law of Conservation of Momentum</h3>
            <p className="text-slate-700 mb-4">The total linear momentum of an <strong>isolated system</strong> remains constant in magnitude and direction.</p>
            <BlockMath math="\Sigma \vec{p}_{initial} = \Sigma \vec{p}_{final}" />
            
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 text-sm">Isolated System</h4>
                <p className="text-xs text-slate-600 mt-1">A system is isolated if the net <strong>external</strong> force acting on it is zero (<InlineMath math="\Sigma \vec{F}_{ext} = 0" />).</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800 text-sm">Internal Forces</h4>
                <p className="text-xs text-slate-600 mt-1">Forces <em>between</em> objects inside the system (Newton's 3rd Law pairs) cancel out and do not change total momentum.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Center of Mass Motion</h3>
            <p className="text-slate-700 mb-4">A powerful tool: in an isolated system, the <strong>velocity of the center of mass (<InlineMath math="v_{cm}" />) remains constant</strong>.</p>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <p className="text-sm text-slate-700"><strong>Example 1:</strong> If two skaters push off each other on frictionless ice, they move apart, but their center of mass remains exactly where they started.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <p className="text-sm text-slate-700"><strong>Example 2:</strong> If a moving grenade explodes in mid-air, the center of mass of all fragments continues along the original parabolic trajectory (ignoring air resistance).</p>
              </div>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-2">COM Velocity Calculation</h4>
              <BlockMath math="\vec{v}_{cm} = \frac{m_1\vec{v}_1 + m_2\vec{v}_2 + ...}{m_1 + m_2 + ...}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Newton’s Third Law Connection</h3>
            <p className="text-slate-700 leading-relaxed">
              Conservation of momentum is a direct consequence of Newton's Third Law:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-sm text-slate-700">
              <li>If A hits B, <InlineMath math="F_{A \to B} = -F_{B \to A}" />.</li>
              <li>Interaction time <InlineMath math="\Delta t" /> is identical.</li>
              <li>Impulse on A = - Impulse on B (<InlineMath math="J_A = -J_B" />).</li>
              <li><InlineMath math="\Delta p_A = - \Delta p_B" />: The gain for one is the loss for the other.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Elastic and Inelastic Collisions",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700">While momentum is conserved in <strong>all</strong> isolated collisions, Kinetic Energy (<InlineMath math="K" />) is not.</p>
          
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Elastic Collisions</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700"><strong>Definition:</strong> Both Momentum and Kinetic Energy are conserved.</p>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs font-mono">
                <div className="bg-white p-2 rounded">Σp_i = Σp_f</div>
                <div className="bg-white p-2 rounded">ΣK_i = ΣK_f</div>
              </div>
              <p className="text-xs text-slate-600 mt-2 italic">Common in the atomic world; billiard balls are macroscopic approximations.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Inelastic Collisions</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <p className="text-sm text-slate-700"><strong>Definition:</strong> Momentum is conserved, but Kinetic Energy is NOT conserved (converted to heat, sound, or deformation).</p>
              <div className="mt-2 text-center text-sm font-bold text-orange-900">
                <InlineMath math="K_f < K_i" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Perfectly Inelastic Collisions</h3>
            <p className="text-slate-700 mb-3">Objects <strong>stick together</strong> and move with a common final velocity. This results in the <strong>maximum possible loss</strong> of Kinetic Energy.</p>
            <div className="bg-slate-800 text-white p-4 rounded-lg text-center">
              <BlockMath math="m_1v_{1i} + m_2v_{2i} = (m_1 + m_2)v_f" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">4. Explosions (Reverse Inelastic)</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm text-slate-700">Mathematically treated as a reverse perfectly inelastic collision. Potential energy is converted into Kinetic Energy (<InlineMath math="K_f > K_i" />). If initially at rest, final momenta must sum to zero.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">5. 2D Collisions (Introduction)</h3>
            <p className="text-slate-700 mb-2">Momentum must be conserved in the X and Y directions independently:</p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-100 p-2 rounded text-center text-xs"><InlineMath math="\Sigma p_{ix} = \Sigma p_{fx}" /></div>
              <div className="bg-slate-100 p-2 rounded text-center text-xs"><InlineMath math="\Sigma p_{iy} = \Sigma p_{fy}" /></div>
            </div>
            <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded italic">
              <strong>Example:</strong> If Object A moving East hits stationary B, and A flies Northeast, B <strong>must</strong> fly Southeast to cancel the new Northward momentum (since initial vertical momentum was zero).
            </p>
          </section>
        </div>
      )
    },
    {
      id: "exam-tips",
      title: "AP Exam Tips & Common Misconceptions",
      content: (
        <div className="space-y-4">
          <Mnemonic 
            title="Bounce vs. Stick" 
            content="Bouncing results in GREATER impulse. Sticking: Δv = 0 - v = -v. Bouncing: Δv = -v - v = -2v. The magnitude of change is doubled!" 
          />
          <div className="space-y-3">
            <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
              <h4 className="font-bold text-rose-900">⚠️ Open vs. Closed Systems</h4>
              <p className="text-xs text-slate-700 mt-1">Define your system! Gravity on a falling ball is an external force (momentum not conserved for "Ball"). For "Ball + Earth," gravity is internal and momentum IS conserved.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
              <h4 className="font-bold text-rose-900">⚠️ Kinetic Energy Trap</h4>
              <p className="text-xs text-slate-700 mt-1">Never assume K is conserved unless it says "elastic." Momentum is your go-to for all collisions; energy is usually lost.</p>
            </div>
            <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
              <h4 className="font-bold text-rose-900">⚠️ Direction Signs</h4>
              <p className="text-xs text-slate-700 mt-1">The most common calculation error is forgetting negative signs for objects moving left or down.</p>
            </div>
          </div>
          <ImportantNote>
            <strong>Area Reminder:</strong> If given a Force vs. Time graph, calculate the area immediately to find <InlineMath math="\Delta p" />. Add this to initial <InlineMath math="p" /> to find the final state.
          </ImportantNote>
        </div>
      )
    }
  ]
};

export default physicsUnit4Notes;